import React from 'react'

import classNames from 'classnames'
import { ErrorMessage, Field, FieldHookConfig, useField } from 'formik'

import { Options } from '@/features/application/applicationSlice'

interface DisabableOption extends Options {
  disabled?: boolean
}

interface CustomProps {
  options: DisabableOption
  label?: string
  labelFlag?: React.ReactNode
  inlineLabel?: boolean
  style?: string
  readOnly?: boolean
  placeholder?: string
  extraClasses?: string
  extraFieldClasses?: string
  extraLabelClasses?: string
  showError?: boolean
  hasPlaceholder?: boolean
  placeholderDefaultValue?: string | number
  onCustomClick?: (e: React.MouseEvent<HTMLSelectElement>) => void
}

type SelectFieldProps = CustomProps &
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLSelectElement>, HTMLSelectElement> &
  FieldHookConfig<string>

/**
 * @deprecated use Field as="select" and customize at the feature level
 * @see useForm() hook
 */
const SelectField = (props: SelectFieldProps) => {
  const {
    label,
    labelFlag,
    inlineLabel = false,
    options,
    style = 'minimal',
    readOnly = false,
    placeholder,
    hasPlaceholder = true,
    placeholderDefaultValue = '', // *sigh*
    extraClasses,
    extraFieldClasses,
    extraLabelClasses,
    showError = true,
    ...fieldHookConfig
  } = props
  const [field] = useField(fieldHookConfig)

  const fieldClasses = style === 'minimal' ? 'minimal-input' : 'select select-bordered w-full'

  const readOnlyClasses = 'bg-gray-300/70'

  // Avoid `value` prop on `select` should not be null. Consider using an empty string to clear the component or
  // `undefined` for uncontrolled components.
  field.value = field.value ?? ''

  const wrapperClasses = classNames({
    'flex items-center': inlineLabel,
  })

  const labelClasses = classNames(
    {
      'mr-2': inlineLabel,
      block: !inlineLabel,
      'mb-2': !inlineLabel,
    },
    'text-sm text-gray-600 flex',
    extraLabelClasses
  )

  const renderOption = ({ value, label, disabled, options }) => {
    if (options) {
      return (
        <>
          <optgroup
            key={label}
            label={label}
            className=''
          >
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </optgroup>
        </>
      )
    }

    return (
      <option
        key={`${value}`}
        value={`${value}`}
        disabled={disabled ? disabled : readOnly}
      >
        {label}
      </option>
    )
  }

  return (
    <div className={`${wrapperClasses} ${extraClasses}`}>
      {!!label && (
        <label
          htmlFor={fieldHookConfig.name}
          className={labelClasses}
        >
          {label}
          {labelFlag}
        </label>
      )}

      <Field
        {...field}
        {...fieldHookConfig}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          field.onChange(e)
          fieldHookConfig.onChange && fieldHookConfig.onChange(e)
        }}
        onClick={(e: React.MouseEvent<HTMLSelectElement>) =>
          fieldHookConfig.onCustomClick && fieldHookConfig.onCustomClick(e)
        }
        as='select'
        // Default option should be gray
        className={`truncate !pr-[2.5rem] text-gray-500 ${fieldClasses} ${extraFieldClasses} ${
          props.readOnly ? readOnlyClasses : ''
        } ${field.value === '' ? 'text-grey-500' : ''}`}
      >
        {hasPlaceholder && (
          // @deprecated Placeholder should not be a substitute for Label,
          // select components don't have placeholders in web standards.
          // This pattern introduced issues when the incoming value is null
          // creating a race condition with the placeholder value which is also null.
          <option
            defaultValue='true'
            value={placeholderDefaultValue}
            disabled
          >
            {placeholder ? placeholder : 'Select...'}
          </option>
        )}
        {options.map(renderOption)}
      </Field>
      {showError && (
        <ErrorMessage
          component='span'
          name={fieldHookConfig.name}
          className={`mt-1 text-red-600 ${extraClasses}`}
        />
      )}
    </div>
  )
}

export default SelectField
